import React, {useState, useMemo, useEffect} from 'react';
import {DragDropContext, Droppable, Draggable, DropResult} from '@hello-pangea/dnd';
import {useRecordContext} from 'components/record/useRecordContext';
import {Grid, Paper, Typography, Box, Card, CardContent, IconButton, Container, Fade, Grow} from '@mui/material';
import {Delete as DeleteIcon, DragIndicator as DragIcon, ArrowDownward as ArrowDownIcon} from '@mui/icons-material';
import {useFetch, useMutate} from 'hooks/useFetchData';
import {Error, Loader} from 'components/common/General';
import Notification from 'components/common/Notification';

type Control = {
  id: string;
  name: string;
  type: string;
  show: boolean;
  analytic: boolean;
  siCode?: number;
  position?: {
    lat: number;
    lng: number;
  };
  order: number;
  draggableId: string;
};

export default function SubEventControls() {
  const {record: subEvent} = useRecordContext();

  const [selectedControls, setSelectedControls] = useState<Control[]>([]);
  const [recentlyAdded, setRecentlyAdded] = useState<string[]>([]);
  const [recentlyRemoved, setRecentlyRemoved] = useState<string[]>([]);

  const isOrienteering = useMemo(() => subEvent.event.category === 'orienteering', [subEvent.event.category]);

  const availableControls = useMemo<Control[]>(() => subEvent.event.controls || [], [subEvent.event.controls]);

  const {error, data, loading, refetch} = useFetch<{items: Control[]; total: {count: number}}>('GET_LIST', 'SubEventControl', {
    variables: {
      subEventId: subEvent.id
    }
  });
  const [mutate] = useMutate<{items: Control[]}>('UPDATE_MANY', 'SubEventControl', {
    variables: {
      subEventId: subEvent.id
    },
    context: {
      headers: {
        'apollo-require-preflight': true
      }
    }
  });

  useEffect(() => {
    if (data?.items) {
      setSelectedControls(data.items.map((c) => ({...(c as Control), draggableId: String(c.order ?? 0)})));
    }
  }, [data]);

  const updateControls = (newControls: Control[]) => {
    setSelectedControls(newControls);
    if (newControls.length > 0 && typeof mutate === 'function') {
      mutate({variables: {subEventId: subEvent.id, payload: newControls.map((c) => ({id: c.id}))}});
    }
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(selectedControls);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    updateControls(items);
    Notification(`Control ${reorderedItem.name} moved to position ${result.destination.index + 1}`, 'info');
  };

  const addControl = (control: Control) => {
    const newControls = [...selectedControls, {...control, draggableId: selectedControls.length.toString()}];
    updateControls(newControls);

    // Add visual feedback
    setRecentlyAdded((prev) => [...prev, control.id]);
    setTimeout(() => {
      setRecentlyAdded((prev) => prev.filter((id) => id !== control.id));
    }, 2000);

    Notification(`Control ${control.name} added to course`, 'success');
  };

  const removeControl = (draggableId: string) => {
    const controlToRemove = selectedControls.find((c) => c.draggableId === draggableId);
    const newControls = selectedControls.filter((c) => c.draggableId !== draggableId);
    updateControls(newControls);

    if (controlToRemove) {
      // Add visual feedback
      setRecentlyRemoved((prev) => [...prev, controlToRemove.id]);
      setTimeout(() => {
        setRecentlyRemoved((prev) => prev.filter((id) => id !== controlToRemove.id));
      }, 2000);

      Notification(`Control ${controlToRemove.name} removed from course`, 'warning');
    }
  };

  if (loading) return <Loader />;
  if (error) return <Error refetch={refetch} />;

  return (
    <Container maxWidth="xl" sx={{py: 3}}>
      <Grid container spacing={3}>
        {/* Available Controls - Control Description Sheet Style */}
        <Grid size={{xs: 12, md: 6}}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              bgcolor: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: 2
            }}
          >
            <Box sx={{textAlign: 'center', mb: 3}}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: '#495057',
                  mb: 1
                }}
              >
                Available Controls
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#6c757d'
                }}
              >
                Click to add to course
              </Typography>
            </Box>

            <Grid container spacing={2} justifyContent="center">
              {availableControls.map((control) => {
                const isRecentlyRemoved = recentlyRemoved.includes(control.id);
                return (
                  <Grid key={control.id}>
                    <Grow in={!isRecentlyRemoved} timeout={300}>
                      <Card
                        sx={{
                          width: 70,
                          height: 70,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: isRecentlyRemoved ? '#6c757d' : 'transparent',
                          background: isOrienteering ? 'linear-gradient(135deg, white 50%, #FF0066 50%)' : 'transparent',
                          color: isRecentlyRemoved ? 'white' : '#e91e63',
                          border: '2px solid',
                          borderColor: isRecentlyRemoved ? '#6c757d' : '#e91e63',
                          borderRadius: 1.5,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          opacity: isRecentlyRemoved ? 0.6 : 1,
                          position: 'relative',
                          overflow: 'hidden',
                          '&:hover': {
                            bgcolor: isRecentlyRemoved ? '#6c757d' : 'transparent',
                            borderColor: isRecentlyRemoved ? '#6c757d' : '#e91e63',
                            transform: isRecentlyRemoved ? 'none' : 'translateY(-2px)',
                            boxShadow: isRecentlyRemoved ? '0 2px 4px rgba(0,0,0,0.1)' : '0 4px 8px rgba(0,0,0,0.15)',
                            transition: 'all 0.2s ease'
                          },
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => !isRecentlyRemoved && addControl(control)}
                      >
                        <CardContent sx={{p: 1, textAlign: 'center', zIndex: 1, position: 'relative'}}>
                          <Typography
                            variant="h6"
                            component="div"
                            sx={{
                              fontWeight: 700,
                              fontFamily: 'monospace',
                              color: isRecentlyRemoved ? 'white' : '#333'
                            }}
                          >
                            {control.siCode || control.name}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grow>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>

        {/* Course Controls - Orienteering Course Style */}
        <Grid size={{xs: 12, md: 6}}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              bgcolor: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: 2
            }}
          >
            <Box sx={{textAlign: 'center', mb: 3}}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: '#495057',
                  mb: 1
                }}
              >
                Course Design
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#6c757d'
                }}
              >
                {selectedControls.length} controls • Drag to reorder
              </Typography>
            </Box>

            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="selectedControls">
                {(provided) => (
                  <Box {...provided.droppableProps} ref={provided.innerRef}>
                    {selectedControls.map((control, index) => {
                      const isRecentlyAdded = recentlyAdded.includes(control.id);
                      return (
                        <Draggable key={control.draggableId} draggableId={control.draggableId} index={index}>
                          {(provided) => (
                            <Fade in={true} timeout={500}>
                              <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  mb: 0.5
                                }}
                              >
                                {/* Control Box */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 2,
                                    bgcolor: isRecentlyAdded ? '#d4edda' : 'white',
                                    borderRadius: 1.5,
                                    border: '2px solid',
                                    borderColor: isRecentlyAdded ? '#28a745' : '#dee2e6',
                                    boxShadow: isRecentlyAdded ? '0 4px 12px rgba(40, 167, 69, 0.2)' : '0 1px 3px rgba(0,0,0,0.08)',
                                    '&:hover': {
                                      borderColor: isRecentlyAdded ? '#28a745' : '#adb5bd',
                                      boxShadow: isRecentlyAdded ? '0 6px 16px rgba(40, 167, 69, 0.3)' : '0 2px 6px rgba(0,0,0,0.12)',
                                      transition: 'all 0.2s ease'
                                    },
                                    transition: 'all 0.2s ease',
                                    animation: isRecentlyAdded ? 'pulse 2s ease-in-out' : 'none',
                                    '@keyframes pulse': {
                                      '0%': {
                                        transform: 'scale(1)'
                                      },
                                      '50%': {
                                        transform: 'scale(1.02)'
                                      },
                                      '100%': {
                                        transform: 'scale(1)'
                                      }
                                    }
                                  }}
                                >
                                  {/* Order Index */}
                                  {isOrienteering && (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 36,
                                        height: 36,
                                        borderRadius: '50%',
                                        bgcolor: index === 0 ? '#28a745' : index === selectedControls.length - 1 ? '#dc3545' : '#007bff',
                                        color: 'white',
                                        mr: 2,
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        border: '2px solid',
                                        borderColor: index === 0 ? '#28a745' : index === selectedControls.length - 1 ? '#dc3545' : '#007bff'
                                      }}
                                    >
                                      {index === 0 ? 'S' : index === selectedControls.length - 1 ? 'F' : index}
                                    </Box>
                                  )}

                                  {/* Drag Handle */}
                                  <Box {...provided.dragHandleProps} sx={{mr: 2, color: '#6c757d', cursor: 'grab'}}>
                                    <DragIcon sx={{fontSize: 20}} />
                                  </Box>

                                  {/* Control Box */}
                                  <Card
                                    sx={{
                                      width: 50,
                                      height: 50,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      bgcolor: 'transparent',
                                      color: '#333',
                                      mr: 2,
                                      background: isOrienteering ? 'linear-gradient(135deg, white 50%, #FF0066 50%)' : 'transparent',
                                      border: '2px solid',
                                      borderColor: isOrienteering ? '#FF0066' : '#e91e63',
                                      borderRadius: 1,
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                      position: 'relative',
                                      overflow: 'hidden'
                                    }}
                                  >
                                    <CardContent sx={{p: 1, textAlign: 'center', zIndex: 1, position: 'relative'}}>
                                      <Typography
                                        variant="h5"
                                        component="div"
                                        sx={{
                                          fontWeight: 700,
                                          fontFamily: 'monospace',
                                          fontSize: '1.5rem',
                                          color: '#333'
                                        }}
                                      >
                                        {control.siCode || control.name}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                  {/* Remove Button */}
                                  <IconButton
                                    color="error"
                                    onClick={() => removeControl(control.draggableId)}
                                    size="small"
                                    sx={{
                                      color: '#dc3545',
                                      '&:hover': {
                                        bgcolor: 'rgba(220, 53, 69, 0.1)'
                                      }
                                    }}
                                  >
                                    <DeleteIcon sx={{fontSize: 18}} />
                                  </IconButton>
                                </Box>

                                {/* Arrow below the box (except for last control) */}
                                {index < selectedControls.length - 1 && (
                                  <Box sx={{color: '#6c757d', mt: 1, mb: 1}}>
                                    <ArrowDownIcon sx={{fontSize: 24}} />
                                  </Box>
                                )}
                              </Box>
                            </Fade>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
